import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Banner, Button, Card } from "@magarac/ui";
import type { HoursRange } from "@magarac/shared/schemas/common";
import { HelpIcon } from "../../../components/HelpIcon";
import { WeeklyHoursEditor } from "../../../components/WeeklyHoursEditor";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/settings/hours")({
  component: HoursPage,
});

/** SCH-2 business hours (settings.hours). Screen key: settings.hours */
function HoursPage() {
  const { active } = useTenant();
  const { query, save, value } = useSetting("hours");
  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Business hours</h1>
        <HelpIcon screen="settings.hours" />
      </div>
      {query.isPending ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <HoursForm
          key={active?.id ?? "none"}
          initial={value?.weekly ?? []}
          onSave={(weekly) => save.mutateAsync({ weekly })}
          busy={save.isPending}
          error={save.error ? messageFor(save.error) : null}
          saved={save.isSuccess}
        />
      )}
    </div>
  );
}

function HoursForm({
  initial,
  onSave,
  busy,
  error,
  saved,
}: {
  initial: HoursRange[];
  onSave: (w: HoursRange[]) => Promise<unknown>;
  busy: boolean;
  error: string | null;
  saved: boolean;
}) {
  const [weekly, setWeekly] = useState<HoursRange[]>(initial);
  const invalid = weekly.some((r) => r.endMin <= r.startMin);
  return (
    <>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {saved ? <Banner tone="ok">Saved. New bookings follow these hours right away.</Banner> : null}
      <Card>
        <p className="mb-4 text-sm text-muted">
          When your business takes visits. Each team member can have their own hours inside these.
        </p>
        <WeeklyHoursEditor value={weekly} onChange={setWeekly} />
        {invalid ? (
          <p className="mt-2 text-sm text-danger">End time must be after start time.</p>
        ) : null}
        <div className="mt-4">
          <Button onClick={() => void onSave(weekly)} busy={busy} disabled={invalid}>
            Save hours
          </Button>
        </div>
      </Card>
    </>
  );
}
